import { useCallback, useEffect, useMemo, useState } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { Box, Chip, Typography } from '@mui/material';
import { handlePriceWithRM } from '../../utils/priceUtils';
import { getMyClaims } from '../../api/incentiveClaimApi';
import useTableState from '../../hooks/useTableState';

const STATUS_CHIP = {
    'In Progress': { bg: '#FEF3C7', color: '#92400E' },
    Eligible: { bg: '#E0E7FF', color: '#3730A3' },
    'On Slip': { bg: '#CCFBF1', color: '#115E59' },
    Completed: { bg: '#D1FAE5', color: '#065F46' },
    Void: { bg: '#FEE2E2', color: '#991B1B' },
    Archived: { bg: '#E2E8F0', color: '#475569' },
};

const StatusChip = ({ value }) => {
    const cfg = STATUS_CHIP[value] || { bg: '#F1F5F9', color: '#64748B' };
    return (
        <Chip
            label={value || '—'}
            size="small"
            sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600, fontFamily: "'Poppins', sans-serif", backgroundColor: cfg.bg, color: cfg.color }}
        />
    );
};

export default function MyIncentiveClaimsList() {
    const ts = useTableState('', { defaultSorting: [{ id: 'salesId', desc: true }] });
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const r = await getMyClaims();
            const all = Array.isArray(r.data) ? r.data : [];
            setData(all.filter(d => (d.rowType ?? 'Incentive') === 'Incentive'));
        } catch (e) {
            console.error('Failed to load my incentive claims:', e);
            setData([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const columns = useMemo(() => [
        { id: 'salesId', header: 'Sales #', accessorKey: 'salesId', size: 80 },
        { id: 'customerName', header: 'Customer', accessorFn: row => row.customerName ?? '', size: 160 },
        { id: 'campaignName', header: 'Campaign', accessorFn: row => row.campaignName ?? '', size: 170 },
        {
            id: 'contribution', header: 'Contribution', size: 110,
            accessorFn: row => row.contribution ?? 0,
            Cell: ({ row }) => {
                const v = row.original.contribution;
                return v != null ? new Intl.NumberFormat('en-MY', { maximumFractionDigits: 2 }).format(v) : '—';
            },
        },
        {
            id: 'amountReward', header: 'Amount / Reward', size: 150,
            accessorFn: row => row.rewardType === 'Cash' ? handlePriceWithRM(row.amount ?? 0) : (row.rewardLabel ?? ''),
            Cell: ({ row }) => {
                const r = row.original;
                if (r.rewardType === 'Cash') return r.amount != null ? handlePriceWithRM(r.amount) : handlePriceWithRM(0);
                return r.rewardLabel || '—';
            },
        },
        {
            id: 'status', header: 'Status', size: 100,
            accessorFn: row => row.displayStatus ?? 'In Progress',
            Cell: ({ row }) => <StatusChip value={row.original.displayStatus} />,
        },
    ], []);

    const table = useMaterialReactTable({
        columns,
        data,
        enableColumnActions: false,
        enableColumnDragging: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        enableStickyHeader: true, muiTableContainerProps: { sx: { maxHeight: 'unset' } },
        initialState: { density: 'compact', showGlobalFilter: true },
        state: {
            isLoading: loading,
            globalFilter: ts.globalFilter,
            sorting: ts.sorting,
            pagination: ts.pagination,
            columnFilters: ts.columnFilters,
        },
        onGlobalFilterChange: ts.setGlobalFilter,
        onSortingChange: ts.setSorting,
        onPaginationChange: ts.setPagination,
        onColumnFiltersChange: ts.setColumnFilters,
        muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
        muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: "'Poppins', sans-serif", padding: '8px 10px' } },
        muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: "'Poppins', sans-serif", padding: '6px 10px' } },
        muiTableBodyRowProps: { sx: { '&:hover': { backgroundColor: '#F8FAFC' } } },
        muiTopToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', minHeight: '48px' } },
        muiBottomToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0' } },
        renderBottomToolbarCustomActions: ({ table }) => {
            const rows = table.getFilteredRowModel().rows.map(r => r.original);
            const pendingRM = rows.filter(r => r.rewardType === 'Cash' && r.displayStatus === 'In Progress').reduce((s, r) => s + (r.amount || 0), 0);
            const paidRM = rows.filter(r => r.rewardType === 'Cash' && r.displayStatus === 'Completed').reduce((s, r) => s + (r.amount || 0), 0);
            return (
                <Typography component="div" sx={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: '#475569', fontFamily: "'Poppins', sans-serif", pl: '8px', flexWrap: 'wrap' }}>
                    <span>Pending: {handlePriceWithRM(pendingRM)}</span>
                    <span>{'·'}</span>
                    <span>Paid: {handlePriceWithRM(paidRM)}</span>
                </Typography>
            );
        },
    });

    return (
        <div>
            <Box sx={{ mb: 2 }}>
                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>My Incentives</h2>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: "'Poppins', sans-serif" }}>Your incentive campaign claims and rewards</p>
            </Box>

            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <MaterialReactTable table={table} />
            </div>
        </div>
    );
}
